import { html } from 'lit'
import { unsafeSVG } from 'lit/directives/unsafe-svg.js'
import { t } from '@/i18n/translate-utils'
import { contentTypeToSvg } from '@/utils/content-type-svg'
import { MIME_TRANSLATION_KEYS } from '@/i18n/mime-keys'

const SUPPORTED_IMAGE_PREVIEW_TYPES = new Set(
  Object.keys(MIME_TRANSLATION_KEYS).filter(mime => mime.startsWith('image/')),
)

function normalizeContentType(contentType: string): string {
  return contentType.toLowerCase().split(';', 1)[0].trim()
}

export function isSupportedImagePreview(contentType: string): boolean {
  const normalizedContentType = normalizeContentType(contentType)
  return !normalizedContentType.startsWith('image/') || SUPPORTED_IMAGE_PREVIEW_TYPES.has(normalizedContentType)
}

export type CompactPreviewMode = 'image' | 'highlighted-icon' | 'neutral-icon'

export function getCompactPreviewMode(
  contentType: string,
  hasThumbnail: boolean,
  hasPreviewError = false,
): CompactPreviewMode {
  const normalizedContentType = normalizeContentType(contentType)
  const isImage = normalizedContentType.startsWith('image/')
  const isPlayableMedia = normalizedContentType.startsWith('video/') || normalizedContentType.startsWith('audio/')
  const isUnsupportedImage = !isSupportedImagePreview(normalizedContentType)

  if (isImage && hasThumbnail && !isUnsupportedImage && !hasPreviewError) {
    return 'image'
  }
  if (isPlayableMedia || isUnsupportedImage || hasPreviewError) {
    return 'highlighted-icon'
  }
  return 'neutral-icon'
}

type IconKind = 'image' | 'video' | 'audio' | 'file'

function getIconKind(contentType: string): IconKind {
  if (contentType.startsWith('image/')) {
    return 'image'
  }
  if (contentType.startsWith('video/')) {
    return 'video'
  }
  if (contentType.startsWith('audio/')) {
    return 'audio'
  }
  return 'file'
}

export function renderPreviewIcon(contentType: string, compact = false, highlighted = false) {
  const normalizedContentType = normalizeContentType(contentType)
  const kind = getIconKind(normalizedContentType)
  const message =
    kind === 'audio' || kind === 'video'
      ? t('gallery.preview.mediaLoadError', 'Download the file and play it on your device.')
      : t('gallery.preview.imageLoadError', 'Download the file and view it on your device.')

  return html`<div
    class="preview-icon-state preview-icon-state-${kind} ${highlighted ? 'preview-icon-state-highlighted' : ''} ${compact
      ? 'preview-icon-state-compact'
      : ''}"
  >
    <div class="preview-icon-state-tile" aria-hidden="true">
      <chameleon-svg>${unsafeSVG(contentTypeToSvg(normalizedContentType))}</chameleon-svg>
    </div>
    ${compact
      ? ''
      : html`<chameleon-typography
          variant="caption-medium"
          color="type-color-secondary"
          >${message}</chameleon-typography
        >`}
  </div>`
}
